import { useCallback, useEffect, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 6;
const EDGE_SCROLL_ZONE_PX = 56;
const EDGE_SCROLL_MAX_PX_PER_FRAME = 18;

const HEADER_SELECTOR = 'th[data-column-key]';
const INTERACTIVE_SELECTOR = 'button, input, a, select, textarea, .tooltip-icon';

const joinClassNames = (...names) => names.filter(Boolean).join(' ');

/** Fallback label: the header's own text nodes (skips tooltips and sort arrows). */
const headerText = (th) => Array.from(th.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent)
    .join(' ')
    .replace(/[↑↓]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Pointer-driven column reordering for a <table> header row.
 *
 * Usage:
 *   const { draggingKey, getHeaderProps } = useColumnDragReorder({ onReorder });
 *   <th {...getHeaderProps(key, { className, onClick })}>…</th>
 *
 * - Press-and-move on a header (past a small threshold) starts a drag. A plain
 *   click still reaches the header's own `onClick` (e.g. sorting).
 * - A floating ghost follows the pointer; a vertical indicator shows where the
 *   column will land. Releasing calls `onReorder(fromKey, toKey, 'before'|'after')`.
 * - When the pointer nears the edge of the nearest horizontally scrollable
 *   ancestor (`scrollContainerSelector`), it auto-scrolls.
 * - Escape cancels. Touch pointers are ignored so mobile scrolling keeps working.
 */
export default function useColumnDragReorder({
    onReorder,
    getLabel,
    scrollContainerSelector = '.table-container',
}) {
    const [draggingKey, setDraggingKey] = useState(null);

    const onReorderRef = useRef(onReorder);
    onReorderRef.current = onReorder;
    const getLabelRef = useRef(getLabel);
    getLabelRef.current = getLabel;

    // Everything about the in-flight gesture lives here so pointermove never
    // triggers a React render.
    const gestureRef = useRef(null);
    const suppressClickRef = useRef(false);

    const cleanupGesture = useCallback(() => {
        const gesture = gestureRef.current;
        if (!gesture) return;

        window.removeEventListener('pointermove', gesture.handleMove);
        window.removeEventListener('pointerup', gesture.handleUp);
        window.removeEventListener('pointercancel', gesture.handleCancel);
        window.removeEventListener('keydown', gesture.handleKey);
        if (gesture.rafId) cancelAnimationFrame(gesture.rafId);

        gesture.ghost?.remove();
        gesture.indicator?.remove();
        document.body.classList.remove('is-column-dragging');

        gestureRef.current = null;
        setDraggingKey(null);
    }, []);

    useEffect(() => cleanupGesture, [cleanupGesture]);

    const startDrag = (gesture) => {
        const { th, key } = gesture;
        const thRect = th.getBoundingClientRect();

        const ghost = document.createElement('div');
        ghost.className = 'column-drag-ghost';
        ghost.textContent = getLabelRef.current?.(key) ?? headerText(th);
        ghost.style.width = `${Math.max(thRect.width, 80)}px`;
        ghost.style.height = `${thRect.height}px`;
        document.body.appendChild(ghost);

        const indicator = document.createElement('div');
        indicator.className = 'column-drop-indicator';
        document.body.appendChild(indicator);

        gesture.ghost = ghost;
        gesture.indicator = indicator;
        gesture.grabOffsetX = gesture.startX - thRect.left;
        gesture.grabOffsetY = gesture.startY - thRect.top;
        gesture.scrollContainer = th.closest(scrollContainerSelector);
        gesture.table = th.closest('table');
        gesture.active = true;

        document.body.classList.add('is-column-dragging');
        setDraggingKey(key);
    };

    /** Work out which header the pointer is over and on which half. */
    const resolveTarget = (gesture) => {
        const headers = Array.from(gesture.th.parentElement.querySelectorAll(HEADER_SELECTOR));
        if (headers.length === 0) return null;

        const x = gesture.lastX;
        const rects = headers.map((header) => header.getBoundingClientRect());
        const first = rects[0];
        const last = rects[rects.length - 1];

        let index;
        let side;
        if (x <= first.left) {
            index = 0;
            side = 'before';
        } else if (x >= last.right) {
            index = headers.length - 1;
            side = 'after';
        } else {
            index = rects.findIndex((rect) => x >= rect.left && x < rect.right);
            if (index === -1) return null;
            const rect = rects[index];
            side = x < rect.left + rect.width / 2 ? 'before' : 'after';
        }

        // Dropping next to itself is a no-op — hide the indicator in that case.
        const fromIndex = headers.indexOf(gesture.th);
        const insertionIndex = index + (side === 'after' ? 1 : 0);
        if (insertionIndex === fromIndex || insertionIndex === fromIndex + 1) return null;

        const rect = rects[index];
        return {
            key: headers[index].dataset.columnKey,
            side,
            x: side === 'before' ? rect.left : rect.right,
        };
    };

    const paint = (gesture) => {
        const { ghost, indicator, table } = gesture;
        ghost.style.transform = `translate3d(${gesture.lastX - gesture.grabOffsetX}px, ${gesture.lastY - gesture.grabOffsetY}px, 0)`;

        const target = resolveTarget(gesture);
        gesture.target = target;

        if (!target) {
            indicator.style.opacity = '0';
            return;
        }

        const tableRect = table.getBoundingClientRect();
        const containerRect = gesture.scrollContainer?.getBoundingClientRect();
        const top = containerRect ? Math.max(tableRect.top, containerRect.top) : tableRect.top;
        const bottom = containerRect ? Math.min(tableRect.bottom, containerRect.bottom) : tableRect.bottom;

        indicator.style.opacity = '1';
        indicator.style.transform = `translate3d(${target.x - 1}px, ${top}px, 0)`;
        indicator.style.height = `${Math.max(bottom - top, 0)}px`;
    };

    const edgeScroll = (gesture) => {
        const container = gesture.scrollContainer;
        if (!container) return false;
        const rect = container.getBoundingClientRect();
        const x = gesture.lastX;
        let delta = 0;

        if (x < rect.left + EDGE_SCROLL_ZONE_PX) {
            const strength = (rect.left + EDGE_SCROLL_ZONE_PX - x) / EDGE_SCROLL_ZONE_PX;
            delta = -Math.min(1, strength) * EDGE_SCROLL_MAX_PX_PER_FRAME;
        } else if (x > rect.right - EDGE_SCROLL_ZONE_PX) {
            const strength = (x - (rect.right - EDGE_SCROLL_ZONE_PX)) / EDGE_SCROLL_ZONE_PX;
            delta = Math.min(1, strength) * EDGE_SCROLL_MAX_PX_PER_FRAME;
        }

        if (delta === 0) return false;
        const before = container.scrollLeft;
        container.scrollLeft = before + delta;
        return container.scrollLeft !== before;
    };

    const frame = (gesture) => {
        if (!gesture.active) return;
        if (edgeScroll(gesture)) paint(gesture);
        gesture.rafId = requestAnimationFrame(() => frame(gesture));
    };

    const handlePointerDown = (event, key) => {
        if (event.button !== 0 || event.pointerType === 'touch') return;
        if (event.target.closest(INTERACTIVE_SELECTOR)) return;
        if (gestureRef.current) return;

        const th = event.currentTarget;
        const gesture = {
            key,
            th,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            active: false,
            target: null,
            rafId: null,
        };

        gesture.handleMove = (moveEvent) => {
            if (moveEvent.pointerId !== gesture.pointerId) return;
            gesture.lastX = moveEvent.clientX;
            gesture.lastY = moveEvent.clientY;

            if (!gesture.active) {
                const dx = moveEvent.clientX - gesture.startX;
                const dy = moveEvent.clientY - gesture.startY;
                if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
                startDrag(gesture);
                gesture.rafId = requestAnimationFrame(() => frame(gesture));
            }

            moveEvent.preventDefault();
            paint(gesture);
        };

        gesture.handleUp = (upEvent) => {
            if (upEvent.pointerId !== gesture.pointerId) return;
            const wasActive = gesture.active;
            const target = gesture.target;
            cleanupGesture();

            if (!wasActive) return;
            // The browser fires `click` right after this pointerup; swallow it so
            // a drag never doubles as a sort click.
            suppressClickRef.current = true;
            setTimeout(() => {
                suppressClickRef.current = false;
            }, 0);
            if (target) onReorderRef.current?.(gesture.key, target.key, target.side);
        };

        gesture.handleCancel = () => cleanupGesture();
        gesture.handleKey = (keyEvent) => {
            if (keyEvent.key === 'Escape') {
                const wasActive = gesture.active;
                cleanupGesture();
                if (wasActive) {
                    suppressClickRef.current = true;
                    setTimeout(() => {
                        suppressClickRef.current = false;
                    }, 0);
                }
            }
        };

        gestureRef.current = gesture;
        window.addEventListener('pointermove', gesture.handleMove, { passive: false });
        window.addEventListener('pointerup', gesture.handleUp);
        window.addEventListener('pointercancel', gesture.handleCancel);
        window.addEventListener('keydown', gesture.handleKey);
    };

    const handleClickCapture = (event) => {
        if (!suppressClickRef.current) return;
        event.preventDefault();
        event.stopPropagation();
    };

    /**
     * Props to spread on a header cell. Pass the cell's existing props so the
     * className is merged rather than replaced.
     */
    const getHeaderProps = useCallback((key, existingProps = {}) => ({
        ...existingProps,
        'data-column-key': key,
        className: joinClassNames(
            existingProps.className,
            'draggable-column',
            draggingKey === key && 'is-dragging',
        ),
        onPointerDown: (event) => handlePointerDown(event, key),
        onClickCapture: handleClickCapture,
    }), [draggingKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return { draggingKey, getHeaderProps };
}
